import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router";
import { Button, Card, Cluster, EmptyState, FormField, Input, Select, Stack, Tabs, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { useAuth } from "../../shell/AuthProvider";
import { useAccess } from "../../shell/AccessProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { SectionNav } from "../../shell/SectionNav";
import { CapabilityGate } from "../crm/CapabilityGate";
import { appUrl } from "../../lib/customerUrl";
import "../crm/crm.css";
import "../pages.css";
import "../workforce.css";
import {
  workforceApi,
  type TimesheetEntry,
  type TimesheetFavorite,
  type TimesheetProject,
  type TimesheetSubmission,
  type TimesheetTimer,
} from "../../lib/workforceApi";
import {
  BILLING_CATEGORIES,
  formatElapsed,
  mondayOf,
  shiftWeek,
  statusBadge,
  todayIso,
  weekDays,
} from "../workforceUi";

function fail(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function TimesheetApp() {
  const { session } = useAuth();
  const { can } = useAccess();
  const orgId = session?.orgId ?? "";
  if (!orgId) return <Navigate to="/dashboard" replace />;
  const userId = session?.userId ?? "";
  const isAdmin = Boolean(session?.isOrgAdmin) || can("timesheet.entries", "delete");
  const href = (path: string) => appUrl("timesheet", session?.orgSlug, path);

  return (
    <CapabilityGate capability="timesheet.entries">
      <div className="crm-app">
        <SectionNav
          label="Timesheet sections"
          items={[
            { to: href("/dashboard"), label: "Dashboard" },
            { to: href("/entries"), label: "Time entries" },
            { to: href("/submissions"), label: "Submissions" },
            { to: href("/manager"), label: "Manager view" },
            { to: href("/projects"), label: "Projects" },
          ]}
        />
        <div className="crm-canvas">
          <Routes>
            <Route path="/" element={<Navigate to={href("/entries")} replace />} />
            <Route path="/dashboard" element={<TimesheetDashboard orgId={orgId} userId={userId} />} />
            <Route path="/entries" element={<TimesheetEntries orgId={orgId} userId={userId} isAdmin={isAdmin} />} />
            <Route path="/submissions" element={<TimesheetSubmissions orgId={orgId} userId={userId} isAdmin={isAdmin} />} />
            <Route path="/manager" element={<TimesheetManager orgId={orgId} userId={userId} />} />
            <Route path="/projects" element={<TimesheetProjects orgId={orgId} />} />
            <Route path="*" element={<Navigate to={href("/entries")} replace />} />
          </Routes>
        </div>
      </div>
    </CapabilityGate>
  );
}

function TimesheetDashboard({ orgId, userId }: { orgId: string; userId: string }) {
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void workforceApi.listTimesheets(orgId).then((result) => setEntries(result.entries)).catch((err) => setError(fail(err, "Could not load entries.")));
  }, [orgId]);
  const mine = entries.filter((entry) => entry.user_id === userId);
  const hours = mine.reduce((sum, entry) => sum + Number(entry.hours), 0);
  const billable = mine.filter((entry) => entry.billable).reduce((sum, entry) => sum + Number(entry.hours), 0);
  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Dashboard</h1>
        <p className="home-subtitle">Hours you have logged, including submitted periods.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <div className="home-kpi-grid">
        <Card><div className="home-kpi-label">Time entries</div><div className="home-kpi-value">{mine.length}</div><div className="home-kpi-delta">Your rows</div></Card>
        <Card><div className="home-kpi-label">Total hours logged</div><div className="home-kpi-value">{hours.toFixed(2)}</div><div className="home-kpi-delta">Open and submitted</div></Card>
        <Card><div className="home-kpi-label">Billable hours</div><div className="home-kpi-value">{billable.toFixed(2)}</div><div className="home-kpi-delta">Marked billable</div></Card>
      </div>
      {mine.length === 0 ? (
        <EmptyState title="No entries yet" description="Log time on Time entries, then submit a period." />
      ) : (
        <Card>
          <h2 className="home-section-title">Recent entries</h2>
          <EntryTable entries={mine.slice(0, 8)} />
        </Card>
      )}
    </Stack>
  );
}

function TimesheetEntries({ orgId, userId, isAdmin }: { orgId: string; userId: string; isAdmin: boolean }) {
  const { show } = useToast();
  const { timezone } = useLocale();
  const today = todayIso(timezone);
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [projects, setProjects] = useState<TimesheetProject[]>([]);
  const [favorites, setFavorites] = useState<TimesheetFavorite[]>([]);
  const [timer, setTimer] = useState<TimesheetTimer | null>(null);
  const [timerStamp, setTimerStamp] = useState(Date.now());
  const [tick, setTick] = useState(Date.now());
  const [mode, setMode] = useState<"single" | "grid">("single");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workDate, setWorkDate] = useState(today);
  const [hours, setHours] = useState("8");
  const [notes, setNotes] = useState("");
  const [projectId, setProjectId] = useState("");
  const [client, setClient] = useState("");
  const [category, setCategory] = useState("Billable");
  const [timerNotes, setTimerNotes] = useState("");
  const [timerProject, setTimerProject] = useState("");
  const [weekOf, setWeekOf] = useState(mondayOf(today));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [gridRows, setGridRows] = useState([{ key: "1", project_id: "", notes: "", days: {} as Record<string, string> }]);

  const load = useCallback(async () => {
    try {
      const [entryResult, projectResult, favoriteResult, timerResult] = await Promise.all([
        workforceApi.listTimesheets(orgId),
        workforceApi.listProjects(orgId),
        workforceApi.listFavorites(orgId),
        workforceApi.getTimer(orgId),
      ]);
      setEntries(entryResult.entries);
      setProjects(projectResult.projects);
      setFavorites(favoriteResult.favorites);
      setTimer(timerResult.timer);
      setTimerStamp(Date.now());
    } catch (err) {
      setError(fail(err, "Timesheets could not be loaded."));
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (timer?.status !== "running") return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [timer?.status]);

  const mine = isAdmin ? entries : entries.filter((entry) => entry.user_id === userId);
  const days = weekDays(weekOf);
  const elapsed = timer
    ? timer.elapsed_seconds + (timer.status === "running" ? Math.floor((tick - timerStamp) / 1000) : 0)
    : 0;

  async function run(action: () => Promise<unknown>, fallback: string, savedMessage?: string) {
    setError(null);
    try {
      await action();
      if (savedMessage) reportSaved(show, savedMessage);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, fallback));
    }
  }

  function applyFavorite(favorite: TimesheetFavorite) {
    setShowForm(true);
    setEditingId(null);
    setMode("single");
    setNotes(favorite.description || favorite.name);
    setProjectId(favorite.project_id ?? "");
    setCategory(favorite.billing_category || "Billable");
  }

  function startEdit(entry: TimesheetEntry) {
    setShowForm(true);
    setEditingId(entry.id);
    setMode("single");
    setWorkDate(entry.work_date);
    setHours(String(entry.hours));
    setNotes(entry.notes ?? "");
    setProjectId(entry.project_id ?? "");
    setClient(entry.client ?? "");
    setCategory(entry.billing_category || (entry.billable ? "Billable" : "Non-billable"));
  }

  function resetEntryForm() {
    setEditingId(null);
    setNotes("");
    setHours("8");
    setWorkDate(today);
    setProjectId("");
    setClient("");
    setCategory("Billable");
  }

  const [entrySearch, setEntrySearch] = useState("");
  const [entryFrom, setEntryFrom] = useState("");
  const [entryTo, setEntryTo] = useState("");
  const needle = entrySearch.trim().toLowerCase();
  const visibleEntries = mine.filter((entry) => {
    if (entryFrom && entry.work_date < entryFrom) return false;
    if (entryTo && entry.work_date > entryTo) return false;
    if (!needle) return true;
    return [entry.notes, entry.project_name, entry.project_code, entry.status].filter(Boolean).join(" ").toLowerCase().includes(needle);
  });

  const openIds = new Set(mine.filter((entry) => entry.status === "draft" && !entry.submission_id).map((entry) => entry.id));
  const chosen = [...selected].filter((id) => openIds.has(id));

  function toggleSelected(id: string, on: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  const submitButton = (
    <span title={chosen.length === 0 ? "Select the time entries you want to submit" : undefined}>
      <Button
        disabled={chosen.length === 0}
        onClick={() => run(async () => {
          for (const id of chosen) await workforceApi.submitTimesheet(orgId, id);
          setSelected(new Set());
        }, "Selected entries could not be submitted.")}
      >
        {chosen.length > 0 ? `Submit selected (${chosen.length})` : "Submit"}
      </Button>
    </span>
  );

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Entries</h1>
        <p className="home-subtitle">Capture a single row, run a timer, or fill a week grid. Submit the period from Submissions.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <Card>
        <Stack gap={3}>
          <Cluster gap={3}>
            <Input value={timerNotes} onChange={(event) => setTimerNotes(event.target.value)} placeholder="What are you working on?" aria-label="Timer description" />
            <Select value={timerProject} onChange={(event) => setTimerProject(event.target.value)} aria-label="Timer project">
              <option value="">Project</option>
              {projects.map((project) => <option key={project.id} value={project.id}>{project.code} — {project.name}</option>)}
            </Select>
            {timer ? (
              <>
                <span className="wf-timer-elapsed" aria-live="polite">{formatElapsed(elapsed)}</span>
                {timer.status === "running" ? (
                  <Button variant="secondary" onClick={() => run(() => workforceApi.pauseTimer(orgId), "Could not pause the timer.")}>Pause</Button>
                ) : (
                  <Button variant="secondary" onClick={() => run(() => workforceApi.resumeTimer(orgId), "Could not resume the timer.")}>Resume</Button>
                )}
                <Button onClick={() => run(() => workforceApi.convertTimer(orgId, today), "Could not convert the timer.")}>Convert → entry</Button>
                <Button variant="ghost" onClick={() => run(() => workforceApi.cancelTimer(orgId), "Could not cancel the timer.")}>Cancel</Button>
              </>
            ) : (
              <Button onClick={() => run(() => workforceApi.startTimer(orgId, { description: timerNotes, project_id: timerProject || undefined }), "Could not start the timer.")}>Start</Button>
            )}
            <Button variant="secondary" onClick={() => {
              setShowForm((open) => {
                if (open) resetEntryForm();
                return !open;
              });
            }}>{showForm ? "Close form" : "+ New time entry"}</Button>
          </Cluster>
          {favorites.length > 0 && (
            <Cluster gap={2}>
              {favorites.map((favorite) => (
                <span key={favorite.id} className="wf-favorite">
                  <Button variant="ghost" onClick={() => applyFavorite(favorite)}>★ {favorite.name}</Button>
                  <Button variant="ghost" onClick={() => run(() => workforceApi.deleteFavorite(orgId, favorite.id), "Could not delete favorite.")}>×</Button>
                </span>
              ))}
            </Cluster>
          )}
        </Stack>
      </Card>
      {showForm && (
        <Card>
          <Stack gap={4}>
            <Tabs
              items={[{ key: "single", label: "Single entry" }, { key: "grid", label: "Week grid" }]}
              activeKey={mode}
              onChange={(key) => setMode(key as "single" | "grid")}
            />
            {mode === "single" ? (
              <Stack gap={3}>
                <Cluster gap={3}>
                  <FormField label="Entry date" htmlFor="ts-date"><Input id="ts-date" type="date" value={workDate} onChange={(event) => setWorkDate(event.target.value)} /></FormField>
                  <FormField label="Hours" htmlFor="ts-hours"><Input id="ts-hours" value={hours} onChange={(event) => setHours(event.target.value)} /></FormField>
                  <FormField label="Project" htmlFor="ts-project">
                    <Select id="ts-project" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                      <option value="">None</option>
                      {projects.map((project) => <option key={project.id} value={project.id}>{project.code} — {project.name}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Billing category" htmlFor="ts-cat">
                    <Select id="ts-cat" value={category} onChange={(event) => setCategory(event.target.value)}>
                      {BILLING_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
                    </Select>
                  </FormField>
                </Cluster>
                <Cluster gap={3}>
                  <FormField label="Description" htmlFor="ts-notes"><Input id="ts-notes" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Description" /></FormField>
                  <FormField label="Client" htmlFor="ts-client"><Input id="ts-client" value={client} onChange={(event) => setClient(event.target.value)} placeholder="Client" /></FormField>
                </Cluster>
                <Cluster gap={2}>
                  <Button
                    onClick={() => run(async () => {
                      const body = {
                        work_date: workDate,
                        hours: Number(hours),
                        notes,
                        project_id: projectId || undefined,
                        client: client || undefined,
                        billing_category: category,
                        billable: category === "Billable",
                      };
                      if (editingId) {
                        await workforceApi.patchTimesheet(orgId, editingId, body);
                      } else {
                        await workforceApi.createTimesheet(orgId, body);
                      }
                      resetEntryForm();
                    }, "Entry could not be saved.", "Saved.")}
                    disabled={!workDate || !hours}
                  >
                    {editingId ? "Save changes" : "Save entry"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => run(() => workforceApi.createFavorite(orgId, {
                      name: notes || "Favorite",
                      description: notes,
                      project_id: projectId || undefined,
                      billing_category: category,
                      billable: category === "Billable",
                    }), "Favorite could not be saved.", "Saved.")}
                    disabled={!notes}
                  >
                    ★ Save
                  </Button>
                  <Button variant="ghost" onClick={() => { resetEntryForm(); setShowForm(false); }}>Cancel</Button>
                </Cluster>
              </Stack>
            ) : (
              <Stack gap={3}>
                <Cluster gap={3} justify="between">
                  <Cluster gap={3}>
                    <Button variant="secondary" onClick={() => setWeekOf(shiftWeek(weekOf, -1))}>←</Button>
                    <strong>Week of {weekOf}</strong>
                    <Button variant="secondary" onClick={() => setWeekOf(shiftWeek(weekOf, 1))}>→</Button>
                  </Cluster>
                  {submitButton}
                </Cluster>
                <p className="wf-muted">Enter hours (e.g. 7.5) per project per day; blank cells are skipped.</p>
                <div className="wf-week-wrap">
                  <table className="crm-table wf-week-grid">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Description</th>
                        {days.map((day) => <th key={day.iso}>{day.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {gridRows.map((row, index) => (
                        <tr key={row.key}>
                          <td>
                            <Select value={row.project_id} onChange={(event) => setGridRows((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, project_id: event.target.value } : item))}>
                              <option value="">Project</option>
                              {projects.map((project) => <option key={project.id} value={project.id}>{project.code}</option>)}
                            </Select>
                          </td>
                          <td>
                            <Input value={row.notes} onChange={(event) => setGridRows((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, notes: event.target.value } : item))} placeholder="Description" />
                          </td>
                          {days.map((day) => (
                            <td key={day.iso}>
                              <Input
                                className="wf-hours-input"
                                style={{ width: "var(--space-8)" }}
                                inputMode="decimal"
                                step="0.25"
                                value={row.days[day.iso] ?? ""}
                                onChange={(event) => setGridRows((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, days: { ...item.days, [day.iso]: event.target.value } } : item))}
                                aria-label={`${row.notes || "Row"} ${day.label}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Cluster gap={2}>
                  <Button variant="secondary" onClick={() => setGridRows((current) => [...current, { key: String(current.length + 1), project_id: "", notes: "", days: {} }])}>+ Row</Button>
                  <Button
                    onClick={() => run(async () => {
                      await workforceApi.saveTimesheetGrid(orgId, gridRows.map((row) => ({
                        notes: row.notes,
                        project_id: row.project_id || undefined,
                        billing_category: category,
                        billable: category === "Billable",
                        days: Object.fromEntries(Object.entries(row.days).map(([iso, value]) => [iso, Number(value)]).filter(([, value]) => Number(value) > 0)),
                      })));
                    }, "Grid could not be saved.", "Saved.")}
                  >
                    Save grid
                  </Button>
                  <Button variant="secondary" onClick={() => { setGridRows([{ key: "1", project_id: "", notes: "", days: {} }]); setShowForm(false); }}>Cancel</Button>
                </Cluster>
              </Stack>
            )}
          </Stack>
        </Card>
      )}
      {mine.length > 0 && (
        <Cluster gap={3}>
          <FormField label="Search entries" htmlFor="ts-search">
            <Input id="ts-search" value={entrySearch} onChange={(event) => setEntrySearch(event.target.value)} placeholder="Description, project or status" />
          </FormField>
          <FormField label="From" htmlFor="ts-from">
            <Input id="ts-from" type="date" value={entryFrom} onChange={(event) => setEntryFrom(event.target.value)} />
          </FormField>
          <FormField label="To" htmlFor="ts-to">
            <Input id="ts-to" type="date" value={entryTo} onChange={(event) => setEntryTo(event.target.value)} />
          </FormField>
          {(entrySearch || entryFrom || entryTo) && (
            <Button variant="ghost" onClick={() => { setEntrySearch(""); setEntryFrom(""); setEntryTo(""); }}>Clear filters</Button>
          )}
        </Cluster>
      )}
      {mine.length === 0 ? (
        <EmptyState title="No entries yet" description="Add a draft for today, then submit the period." />
      ) : visibleEntries.length === 0 ? (
        <EmptyState title="No matching entries" description="Try a different search or date range." />
      ) : (
        <>
          <EntryTable
            entries={visibleEntries}
            showPerson={isAdmin}
            allowDeleteClosed={isAdmin}
            onDelete={(id) => void run(() => workforceApi.deleteTimesheet(orgId, id), "Could not delete entry.")}
            onEdit={startEdit}
            selected={selected}
            onToggle={toggleSelected}
          />
          <Cluster gap={3} justify="end">{submitButton}</Cluster>
        </>
      )}
    </Stack>
  );
}

function entryIsOpen(entry: TimesheetEntry): boolean {
  if (entry.status === "rejected") return true;
  return !entry.submission_id && entry.status === "draft";
}

function EntryTable({ entries, onDelete, onEdit, selected, onToggle, showPerson, allowDeleteClosed }: {
  entries: TimesheetEntry[];
  onDelete?: (id: string) => void;
  onEdit?: (entry: TimesheetEntry) => void;
  selected?: Set<string>;
  onToggle?: (id: string, on: boolean) => void;
  showPerson?: boolean;
  allowDeleteClosed?: boolean;
}) {
  const actions = Boolean(onDelete || onEdit);
  const selectable = Boolean(selected && onToggle);
  return (
    <table className="crm-table">
      <thead>
        <tr>{selectable ? <th></th> : null}{showPerson ? <th>Person</th> : null}<th>Date</th><th>Duration</th><th>Description</th><th>Project</th><th>Status</th><th>Rejection comment</th>{actions ? <th></th> : null}</tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.id}>
            {selectable ? (
              <td>
                {entry.status === "draft" && !entry.submission_id && (
                  <input
                    type="checkbox"
                    checked={selected!.has(entry.id)}
                    onChange={(event) => onToggle!(entry.id, event.target.checked)}
                    aria-label={`Select ${entry.work_date} ${entry.notes || "entry"} to submit`}
                  />
                )}
              </td>
            ) : null}
            {showPerson ? <td>{entry.user_name || "—"}</td> : null}
            <td>{entry.work_date}</td>
            <td>{entry.hours}h</td>
            <td>{entry.notes || "—"}</td>
            <td>{entry.project_name || entry.project_code || "—"}</td>
            <td>
              <span className={statusBadge(entry.status)}>
                {entry.submission_id && entry.status !== "rejected" ? "🔒 Submitted" : entry.status}
              </span>
            </td>
            <td>{entry.decision_comment?.trim() || "—"}</td>
            {actions ? (
              <td>
                {(entryIsOpen(entry) || allowDeleteClosed) && (
                  <Cluster gap={2}>
                    {entryIsOpen(entry) && onEdit && <Button variant="ghost" onClick={() => onEdit(entry)}>Edit</Button>}
                    {(entryIsOpen(entry) || allowDeleteClosed) && onDelete && (
                      <Button variant="ghost" onClick={() => onDelete(entry.id)}>Delete</Button>
                    )}
                  </Cluster>
                )}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TimesheetSubmissions({ orgId, userId, isAdmin }: { orgId: string; userId: string; isAdmin: boolean }) {
  const [items, setItems] = useState<TimesheetSubmission[]>([]);
  const [periodStart, setPeriodStart] = useState(mondayOf(todayIso()));
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems((await workforceApi.listSubmissions(orgId)).submissions);
    } catch (err) {
      setError(fail(err, "Submissions could not be loaded."));
    }
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const mine = isAdmin ? items : items.filter((item) => item.user_id === userId);
  const open = mine.find((item) => item.id === openId);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(fail(err, fallback));
    }
  }

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Submissions</h1>
        <p className="home-subtitle">Lock every open entry in a date range and send it to your manager.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <Card>
        <Cluster gap={3}>
          <FormField label="Period start" htmlFor="ts-start"><Input id="ts-start" type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></FormField>
          <FormField label="Period end" htmlFor="ts-end"><Input id="ts-end" type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></FormField>
          <Button onClick={() => run(() => workforceApi.submitPeriod(orgId, periodStart, periodEnd), "Timesheet could not be submitted.")}>Submit Timesheet</Button>
        </Cluster>
      </Card>
      {mine.length === 0 ? (
        <EmptyState title="No submissions yet" description="Log hours first, then submit a period." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr>{isAdmin ? <th>Person</th> : null}<th>Period</th><th>Entries</th><th>Hours</th><th>Status</th><th>Rejection comment</th><th></th></tr>
          </thead>
          <tbody>
            {mine.map((item) => (
              <tr key={item.id} className="wf-row-clickable" onClick={() => setOpenId(item.id)}>
                {isAdmin ? <td>{item.user_name || "—"}</td> : null}
                <td>{item.period_start} → {item.period_end}</td>
                <td>{item.entry_count}</td>
                <td>{item.hours}</td>
                <td>
                  <span className={statusBadge(item.status)}>{item.status}</span>
                </td>
                <td>{item.decision_comment?.trim() || "—"}</td>
                <td>
                  <Cluster gap={2}>
                    {(item.status === "submitted" || item.status === "rejected") && (
                      <Button variant="secondary" onClick={(event) => { event.stopPropagation(); return run(() => workforceApi.withdrawSubmission(orgId, item.id), "Could not withdraw."); }}>Withdraw</Button>
                    )}
                    {(item.status === "rejected" || item.status === "withdrawn") && (
                      <Button variant="secondary" onClick={(event) => { event.stopPropagation(); return run(() => workforceApi.resubmitSubmission(orgId, item.id), "Could not resubmit."); }}>Resubmit</Button>
                    )}
                    {(item.status === "withdrawn" || isAdmin) && (
                      <Button variant="ghost" onClick={(event) => { event.stopPropagation(); return run(() => workforceApi.deleteSubmission(orgId, item.id), "Could not delete."); }}>Delete</Button>
                    )}
                  </Cluster>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">{open.period_start} → {open.period_end}</h2>
            <p className="wf-muted">{open.entry_count} entries · {open.hours} hours · {open.status}</p>
            <p>Rejection comment: {open.decision_comment?.trim() || "None recorded."}</p>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function TimesheetManager({ orgId, userId }: { orgId: string; userId: string }) {
  const [items, setItems] = useState<TimesheetSubmission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems((await workforceApi.listSubmissions(orgId)).submissions);
    } catch (err) {
      setError(fail(err, "Manager view could not be loaded."));
    }
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const pending = items.filter((item) => item.status === "submitted");
  const rejected = items.filter((item) => item.status === "rejected");
  const actionable = pending.filter((item) => item.user_id !== userId);
  const ownPending = pending.filter((item) => item.user_id === userId);
  const approved = items.filter((item) => item.status === "approved");
  const open = items.find((item) => item.id === openId);
  const canDecideOpen = Boolean(open && open.status === "submitted" && open.user_id !== userId);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
      setComment("");
      await load();
    } catch (err) {
      setError(fail(err, fallback));
    }
  }

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Manager view</h1>
        <p className="home-subtitle">Approve or reject submitted periods. You cannot decide your own timesheet.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <div className="home-kpi-grid">
        <Card><div className="home-kpi-label">Pending approval</div><div className="home-kpi-value">{actionable.length}</div></Card>
        <Card><div className="home-kpi-label">All submissions</div><div className="home-kpi-value">{items.length}</div></Card>
        <Card><div className="home-kpi-label">Approved</div><div className="home-kpi-value">{approved.length}</div></Card>
      </div>
      {ownPending.length > 0 && (
        <p className="wf-muted">Your timesheet is waiting on your manager. You cannot approve or reject it.</p>
      )}
      {actionable.length === 0 ? (
        <EmptyState title="Nothing pending approval." description="Submitted periods from your team appear here." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr><th>Employee</th><th>Period</th><th>Entries</th><th>Hours</th><th></th></tr>
          </thead>
          <tbody>
            {actionable.map((item) => (
              <tr key={item.id}>
                <td>{item.user_name}</td>
                <td>{item.period_start} → {item.period_end}</td>
                <td>{item.entry_count}</td>
                <td>{item.hours}</td>
                <td>
                  <Cluster gap={2}>
                    <Button variant="secondary" onClick={() => run(() => workforceApi.approveSubmission(orgId, item.id), "Approve failed")}>Approve</Button>
                    <Button variant="ghost" onClick={() => setOpenId(item.id)}>Review</Button>
                  </Cluster>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rejected.length > 0 && (
        <Stack gap={3}>
          <h2 className="home-section-title">Rejected</h2>
          <table className="crm-table">
            <thead>
              <tr><th>Employee</th><th>Period</th><th>Rejection comment</th></tr>
            </thead>
            <tbody>
              {rejected.map((item) => (
                <tr key={item.id}>
                  <td>{item.user_name}</td>
                  <td>{item.period_start} → {item.period_end}</td>
                  <td>{item.decision_comment?.trim() || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      )}
      {canDecideOpen && open && (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">{open.user_name} · {open.period_start} → {open.period_end}</h2>
            <FormField label="Note (optional)" htmlFor="ts-note">
              <Input id="ts-note" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Required to reject" />
            </FormField>
            <Cluster gap={2}>
              <Button onClick={() => run(() => workforceApi.approveSubmission(orgId, open.id, comment || undefined), "Approve failed")}>Approve</Button>
              <Button variant="ghost" onClick={() => run(() => workforceApi.rejectSubmission(orgId, open.id, comment), "Reject failed")}>Reject</Button>
            </Cluster>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function TimesheetProjects({ orgId }: { orgId: string }) {
  const [projects, setProjects] = useState<TimesheetProject[]>([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setProjects((await workforceApi.listProjects(orgId, showInactive)).projects);
    } catch (err) {
      setError(fail(err, "Projects could not be loaded."));
    }
  }, [orgId, showInactive]);
  useEffect(() => { void load(); }, [load]);

  const visible = projects;

  return (
    <Stack gap={4}>
      <div>
        <h1 className="home-title">Projects</h1>
        <p className="home-subtitle">Shared codes used on time entries and the week grid.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <Card>
        <Cluster gap={3}>
          <Input value={code} onChange={(event) => setCode(event.target.value)} placeholder="Code" aria-label="Project code" />
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" aria-label="Project name" />
          <Button
            onClick={() => {
              setError(null);
              void workforceApi.createProject(orgId, { code, name }).then(() => {
                setCode("");
                setName("");
                return load();
              }).catch((err) => setError(fail(err, "Project could not be saved.")));
            }}
            disabled={!code || !name}
          >
            Add
          </Button>
          <Button variant="ghost" onClick={() => setShowInactive((value) => !value)}>{showInactive ? "Hide inactive" : "Show inactive"}</Button>
        </Cluster>
      </Card>
      {visible.length === 0 ? (
        <EmptyState title="No projects" description="Add a code and name so the week grid can group hours." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr><th>Code</th><th>Name</th><th>State</th><th></th></tr>
          </thead>
          <tbody>
            {visible.map((project) => (
              <tr key={project.id}>
                <td>{project.code}</td>
                <td>{project.name}</td>
                <td><span className={project.active ? "crm-badge crm-badge--active" : "crm-badge"}>{project.active ? "Active" : "Inactive"}</span></td>
                <td>
                  <Button
                    variant="ghost"
                    onClick={() => workforceApi.patchProject(orgId, project.id, { code: project.code, name: project.name, active: !project.active }).then(load).catch((err) => setError(fail(err, "Could not update project.")))}
                  >
                    {project.active ? "Deactivate" : "Activate"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Stack>
  );
}
